import React, { useState } from 'react';
import { AlertTriangle, Book, Download, Eye, ChevronRight } from 'lucide-react';

const ProcessDescription = () => {
  const [selectedProcess, setSelectedProcess] = useState('distillation');

  const processDescriptions = {
    distillation: {
      name: 'Distillation Unit Process',
      tag: 'U-100',
      operatingIntent: 'Separate crude product into light and heavy fractions using fractional distillation',
      description: 'The distillation unit receives crude product from the reactor section at approximately 150°C and atmospheric pressure. The feed is preheated to 180°C using the bottom product stream before entering the distillation column T-101 at tray 20. The column operates at 5 bar pressure with 40 sieve trays. Light product is withdrawn from the top at 90°C while heavy product exits the bottom at 165°C. The reboiler E-101 provides heat duty of 2.5 MW using steam at 10 bar.',
      operatingConditions: {
        feedTemp: '180°C',
        feedRate: '50 m³/h',
        topTemp: '90°C',
        bottomTemp: '165°C',
        pressure: '5 bar',
        refluxRatio: '3:1'
      },
      safetyConsiderations: [
        'Column pressure must be maintained below 7 bar to prevent overpressure',
        'Reboiler duty must not exceed 3 MW maximum design',
        'Reflux pump failure will trigger automatic shutdown',
        'High temperature alarm at 190°C bottom temperature'
      ],
      normalOperatingProcedure: [
        'Verify all utility services are available (steam, cooling water, nitrogen)',
        'Establish reflux flow and verify level control',
        'Introduce feed gradually while monitoring pressure and temperatures',
        'Adjust reflux ratio to achieve desired product specifications',
        'Monitor product quality and adjust operating parameters as needed'
      ],
      emergencyProcedures: [
        'Loss of cooling: Reduce reboiler duty, increase reflux, if necessary shutdown',
        'High pressure: Vent to flare system, reduce reboiler duty',
        'Loss of reflux: Shutdown reboiler immediately, introduce emergency cooling',
        'Feed contamination: Divert feed, flush column with clean solvent'
      ]
    },
    reaction: {
      name: 'Reaction Process',
      tag: 'U-200',
      operatingIntent: 'Convert raw materials to desired product through catalytic reaction in controlled environment',
      description: 'The CSTR reactor R-201 operates continuously at 180°C and 15 bar. Reactants are fed at controlled rates and mixed with recycled catalyst. The reaction is exothermic with heat removal through jacket cooling. Residence time is maintained at 2 hours for complete conversion. Product stream exits through the top outlet and proceeds to separation.',
      operatingConditions: {
        temperature: '180°C',
        pressure: '15 bar',
        feedRate: '100 m³/h',
        resTime: '2 hours',
        agitator: '75 RPM',
        cooling: 'Jacket, -500 kW'
      },
      safetyConsiderations: [
        'Temperature runaway risk - continuous cooling water monitoring required',
        'Pressure relief set at 20 bar to flare system',
        'Agitator failure triggers emergency cooling and feed shutdown',
        'Catalyst poisoning detection through conversion monitoring'
      ],
      normalOperatingProcedure: [
        'Verify cooling system operational and temperature stable',
        'Start agitator and establish circulation',
        'Introduce catalyst at specified rate',
        'Begin feed introduction gradually',
        'Monitor conversion and adjust feed rates to maintain conditions'
      ],
      emergencyProcedures: [
        'High temperature: Increase cooling, reduce feed, emergency quench if > 200°C',
        'Loss of cooling: Emergency shutdown, activate emergency cooling',
        'High pressure: Vent to flare, reduce feed, initiate shutdown',
        'Agitator failure: Stop feed, activate emergency cooling, depressurize safely'
      ]
    }
  };

  const processes = [
    { id: 'distillation', name: 'Distillation Unit', tag: 'U-100' },
    { id: 'reaction', name: 'Reaction Process', tag: 'U-200' }
  ];

  const currentProcess = processDescriptions?.[selectedProcess];

  return (
    <div className="space-y-6">
      {/* Process Selector */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center space-x-4 overflow-x-auto">
          {processes?.map((process) => (
            <button
              key={process.id}
              onClick={() => setSelectedProcess(process.id)}
              className={`flex items-center space-x-2 px-4 py-3 rounded-lg whitespace-nowrap transition-colors ${
                selectedProcess === process.id
                  ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Book className="w-4 h-4" />
              <span className="font-medium text-sm">{process.name}</span>
              <span className="text-xs opacity-75">({process.tag})</span>
            </button>
          ))}
        </div>
      </div>
      {/* Process Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{currentProcess?.name}</h2>
            <p className="text-sm text-gray-600 mt-1">Process Tag: {currentProcess?.tag}</p>
          </div>
          <div className="flex items-center space-x-2">
            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg">
              <Eye className="w-5 h-5" />
            </button>
            <button className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg">
              <Download className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded-r-lg">
          <h3 className="font-semibold text-blue-900 mb-2">Operating Intent</h3>
          <p className="text-blue-800">{currentProcess?.operatingIntent}</p>
        </div>
      </div>
      {/* Process Description */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Process Description</h3>
        <p className="text-gray-700 leading-relaxed">{currentProcess?.description}</p>
      </div>
      {/* Operating Conditions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Normal Operating Conditions</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {Object.entries(currentProcess?.operatingConditions)?.map(([key, value]) => (
            <div key={key} className="bg-gray-50 p-4 rounded-lg">
              <p className="text-xs text-gray-600 uppercase mb-1">{key?.replace(/([A-Z])/g, ' $1')?.trim()}</p>
              <p className="text-lg font-semibold text-gray-900">{value}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Safety Considerations */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center space-x-2 mb-4">
          <AlertTriangle className="w-6 h-6 text-red-600" />
          <h3 className="text-lg font-bold text-gray-900">Safety Considerations</h3>
        </div>
        <div className="space-y-3">
          {currentProcess?.safetyConsiderations?.map((consideration, index) => (
            <div key={index} className="flex items-start space-x-3 p-3 bg-red-50 rounded-lg">
              <ChevronRight className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-gray-800">{consideration}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Normal Operating Procedure */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Normal Operating Procedure</h3>
          <div className="space-y-3">
            {currentProcess?.normalOperatingProcedure?.map((step, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  {index + 1}
                </div>
                <p className="text-gray-700 pt-0.5">{step}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Emergency Procedures */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center space-x-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h3 className="text-lg font-bold text-gray-900">Emergency Procedures</h3>
          </div>
          <div className="space-y-3">
            {currentProcess?.emergencyProcedures?.map((procedure, index) => (
              <div key={index} className="p-3 bg-red-50 border-l-4 border-red-600 rounded-r-lg">
                <p className="text-gray-800 font-medium">{procedure}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcessDescription;